import { isFunction, forEach, assign } from 'min-dash';

export var DEFAULT_LABEL_SIZE = {
  width: 90,
  height: 20
};

export var FLOW_LABEL_INDENT = 15;

export function wrapForCompatibility(api) {

  return function() {

    if (!window.Promise) {
      throw new Error('Promises is not supported in this environment. Please polyfill Promise.');
    }

    var argLen = arguments.length;
    if (argLen >= 1 && isFunction(arguments[argLen - 1])) {

      var callback = arguments[argLen - 1];

      console.warn(new Error(
        'Passing callbacks to ' + api.name + ' is deprecated.'
      ));

      var argsWithoutCallback = Array.prototype.slice.call(arguments, 0, -1);

      api.apply(this, argsWithoutCallback).then(function(result) {

        var firstKey = Object.keys(result)[0];

        return callback(null, result[firstKey]);

      }, function(err) {

        return callback(err, err.warnings);
      });
    } else {

      return api.apply(this, arguments);
    }
  };
}

export function isExpanded(element) {

  if (is(element, 'bpmn:CallActivity')) {
    return false;
  }

  if (is(element, 'bpmn:SubProcess')) {
    return getBusinessObject(element).di && !!getBusinessObject(element).di.isExpanded;
  }

  if (is(element, 'bpmn:Participant')) {
    return !!getBusinessObject(element).processRef;
  }

  return true;
}

export function isInterrupting(element) {
  return element && getBusinessObject(element).isInterrupting !== false;
}

export function isEventSubProcess(element) {
  return element && !!getBusinessObject(element).triggeredByEvent;
}

export function hasEventDefinition(element, eventType) {
  var bo = getBusinessObject(element),
      hasEventDefinition = false;

  if (bo.eventDefinitions) {
    forEach(bo.eventDefinitions, function(event) {
      if (is(event, eventType)) {
        hasEventDefinition = true;
      }
    });
  }

  return hasEventDefinition;
}

export function hasErrorEventDefinition(element) {
  return hasEventDefinition(element, 'bpmn:ErrorEventDefinition');
}

export function hasEscalationEventDefinition(element) {
  return hasEventDefinition(element, 'bpmn:EscalationEventDefinition');
}

export function hasCompensateEventDefinition(element) {
  return hasEventDefinition(element, 'bpmn:CompensateEventDefinition');
}

export function isLabelExternal(semantic) {
  return is(semantic, 'bpmn:Event') ||
         is(semantic, 'bpmn:Gateway') ||
         is(semantic, 'bpmn:DataStoreReference') ||
         is(semantic, 'bpmn:DataObjectReference') ||
         is(semantic, 'bpmn:DataInput') ||
         is(semantic, 'bpmn:DataOutput') ||
         is(semantic, 'bpmn:SequenceFlow') ||
         is(semantic, 'bpmn:MessageFlow') ||
         is(semantic, 'bpmn:Group');
}

export function hasExternalLabel(element) {
  return isLabel(element.label);
}

export function getFlowLabelPosition(waypoints) {
  const mid = waypoints.length / 2 - 1;
  const firstIndex = Math.floor(mid);
  const secondIndex = Math.ceil(mid + 0.01);

  const first = waypoints[firstIndex];
  const second = waypoints[secondIndex];

  const position = getWaypointsMid(waypoints);
  const angle = Math.atan((second.y - first.y) / (second.x - first.x));

  let x = position.x;
  let y = position.y;

  if (Math.abs(angle) < Math.PI / 2) {
    y -= FLOW_LABEL_INDENT;
  } else {
    x += FLOW_LABEL_INDENT;
  }

  return { x, y };
}

export function getWaypointsMid(waypoints) {
  const mid = waypoints.length / 2 - 1;
  const firstIndex = Math.floor(mid);
  const secondIndex = Math.ceil(mid + 0.01);

  const first = waypoints[firstIndex];
  const second = waypoints[secondIndex];

  const x = first.x + (second.x - first.x) / 2;
  const y = first.y + (second.y - first.y) / 2;

  return { x, y };
}

export function getExternalLabelMid(element) {
  if (element.waypoints) {
    return getFlowLabelPosition(element.waypoints);
  }

  const isGroup = is(element, 'bpmn:Group');
  const isDefaultLabelPosition = isGroup ? DEFAULT_LABEL_SIZE.height / 2 : element.height + DEFAULT_LABEL_SIZE.height / 2;

  return {
    x: element.x + element.width / 2,
    y: element.y + isDefaultLabelPosition
  };
}

export function getExternalLabelBounds(semantic, element) {
  const di = semantic.di;
  const label = di.label;

  if (label && label.bounds) {
    const bounds = label.bounds;

    const size = {
      width: Math.max(DEFAULT_LABEL_SIZE.width, bounds.width),
      height: bounds.height
    };

    const mid = {
      x: bounds.x + bounds.width / 2,
      y: bounds.y + bounds.height / 2
    };

    return assign({
      x: mid.x - size.width / 2,
      y: mid.y - size.height / 2
    }, size);
  }

  const mid = getExternalLabelMid(element);
  const size = DEFAULT_LABEL_SIZE;

  return assign({
    x: mid.x - size.width / 2,
    y: mid.y - size.height / 2
  }, size);
}

export function isLabel(element) {
  return element && !!element.labelTarget;
}

export function is(element, type) {
  var bo = getBusinessObject(element);

  return bo && (typeof bo.$instanceOf === 'function') && bo.$instanceOf(type);
}

export function getBusinessObject(element) {
  return (element && element.businessObject) || element;
}